import { type ActionKind, ALWAYS_NEVER, type FloorKind, type PolicyMode, type PolicyRule } from "@pagerless/schema";
import { useState } from "react";
import { ScreenHead } from "../components/ScreenHead";
import { Segmented, type SegmentOption } from "../components/Segmented";
import { ErrorNote, Loading } from "../components/Shell";
import { useDeps, useResource } from "../context";
import { rulesPager } from "../lib/pager";
import { presetOf } from "./Setup";

export type RuleRow = {
  id: string;
  title: string;
  kind: ActionKind;
  /** A row scoped to these floor signals (e.g. "an update that broke payments"). */
  floor?: FloorKind[];
  /** Can't be undone: the helper always asks, so "Just do it" is off. */
  irreversible?: boolean;
};

export const RULE_ROWS: RuleRow[] = [
  { id: "restart", title: "Restart something that's stuck", kind: "restart" },
  { id: "add_capacity", title: "Add capacity when the app is busy", kind: "add_capacity" },
  {
    id: "undo_payments",
    title: "Undo an update that broke payments",
    kind: "undo_update",
    floor: ["payments_failing"],
  },
  { id: "undo_any", title: "Undo any other update", kind: "undo_update" },
  { id: "open_pr", title: "Write a proper fix for review", kind: "open_pr" },
  { id: "pause", title: "Pause the app", kind: "pause" },
  { id: "email", title: "Email customers", kind: "email_customers", irreversible: true },
  { id: "data", title: "Change or delete saved data", kind: "data_change", irreversible: true },
];

const sameFloor = (a: FloorKind[] | undefined, b: FloorKind[] | undefined) =>
  JSON.stringify([...(a ?? [])].sort()) === JSON.stringify([...(b ?? [])].sort());

function generalRule(rules: PolicyRule[], kind: ActionKind): PolicyRule | undefined {
  return rules.find((r) => r.kind === kind && !r.when?.floor);
}

/** The mode a row shows: its own rule, else the general rule for the kind, else "ask". */
export function modeFor(row: RuleRow, rules: PolicyRule[]): PolicyMode {
  if (ALWAYS_NEVER.includes(row.kind)) return "never";
  if (row.floor) {
    const own = rules.find((r) => r.kind === row.kind && sameFloor(r.when?.floor, row.floor));
    if (own) return own.mode;
  }
  return generalRule(rules, row.kind)?.mode ?? "ask";
}

/** The rule to save when a row changes. General rows keep their other limits (e.g. up to 2×). */
export function ruleFor(row: RuleRow, mode: PolicyMode, rules: PolicyRule[]): PolicyRule {
  if (row.floor) return { kind: row.kind, mode, when: { floor: row.floor } };
  const existing = generalRule(rules, row.kind);
  return existing?.when ? { kind: row.kind, mode, when: existing.when } : { kind: row.kind, mode };
}

function applyLocal(rules: PolicyRule[], rule: PolicyRule): PolicyRule[] {
  const slot = (r: PolicyRule) => r.kind === rule.kind && sameFloor(r.when?.floor, rule.when?.floor);
  return rules.some(slot) ? rules.map((r) => (slot(r) ? rule : r)) : [...rules, rule];
}

export function Rules() {
  const { api } = useDeps();
  const rules = useResource((a) => a.rules());
  const [saving, setSaving] = useState<string | null>(null);
  const [changed, setChanged] = useState<Set<string>>(new Set());
  const [error, setError] = useState<Error | null>(null);

  if (rules.status === "loading") return <Loading what="Loading your rules" />;
  if (rules.status === "error") return <ErrorNote error={rules.error} retry={rules.reload} />;
  const current = rules.data;
  const preset = presetOf(current);

  async function change(row: RuleRow, mode: PolicyMode) {
    const before = current;
    const rule = ruleFor(row, mode, before);
    setError(null);
    setSaving(row.id);
    rules.set(applyLocal(before, rule)); // optimistic; rolled back on failure
    try {
      rules.set(await api.setRule(rule));
      setChanged((s) => new Set(s).add(row.id));
    } catch (e) {
      rules.set(before);
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setSaving(null);
    }
  }

  return (
    <>
      <title>Helper rules · Pagerless</title>
      <ScreenHead
        pager={rulesPager(
          RULE_ROWS.map((r) => modeFor(r, current)),
          preset,
        )}
        title="What your helper may do"
        sub="Only you can change these."
      />
      {error && <ErrorNote error={error} />}
      <ul className="box list">
        {RULE_ROWS.map((row) => {
          const locked = ALWAYS_NEVER.includes(row.kind);
          const factor =
            row.kind === "add_capacity" ? generalRule(current, "add_capacity")?.when?.maxCapacityFactor : undefined;
          const detail = locked
            ? "Always off. Locked."
            : row.irreversible
              ? "Can't be undone: always asks"
              : factor
                ? `Up to ${factor}× · costs extra while on`
                : changed.has(row.id)
                  ? "Changed just now"
                  : null;
          const descId = `rule-${row.id}-desc`;
          const options: SegmentOption<PolicyMode>[] = [
            { value: "alone", label: "Just do it", disabled: locked || row.irreversible },
            { value: "ask", label: "Ask me", disabled: locked },
            { value: "never", label: "Never", className: "never" },
          ];
          return (
            <li key={row.id} className={`row rule${changed.has(row.id) ? " changed" : ""}`}>
              <div>
                <b id={`rule-${row.id}`}>{row.title}</b>
                {detail && (
                  <p className="muted" id={descId}>
                    {detail}
                  </p>
                )}
              </div>
              <Segmented
                label={row.title}
                options={options}
                value={modeFor(row, current)}
                onChange={(m) => void change(row, m)}
                describedBy={detail ? descId : undefined}
                busy={saving === row.id}
              />
            </li>
          );
        })}
      </ul>
    </>
  );
}
